/**
 * Screenshot Tool
 *
 * Allows taking screenshots of the point cloud via area selection.
 * Forces 2D orthographic view for consistent screenshot capture.
 * Includes camera alignment (2-click rotation calibration).
 */

import { BaseTool, DragEventData } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona, IViewConfig } from '../../types';
import { ViewModes } from '../../helpers';
import { CameraAlignmentTool } from '../../../utils/CameraAlignmentTool';
import * as THREE from 'three';

export class ScreenshotPersonaTool extends BaseTool {
  readonly name = 'screenshot';

  private selectionBox: HTMLDivElement;
  private selectionStart: THREE.Vector2 = new THREE.Vector2();
  private isSelecting = false;
  private savedPointBudget = 3_000_000;

  private cameraAlignmentTool: CameraAlignmentTool;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    this.cameraAlignmentTool = new CameraAlignmentTool(viewer);

    this.selectionBox = document.createElement('div');
    this.selectionBox.style.position = 'absolute';
    this.selectionBox.style.border = '2px dashed white';
    this.selectionBox.style.background = 'rgba(100, 100, 100, 0.2)';
    this.selectionBox.style.display = 'none';
    this.selectionBox.style.pointerEvents = 'none';
  }

  protected onEnable(): void {
    this.viewer.renderArea.style.cursor = 'crosshair';
    this.viewer.renderArea.appendChild(this.selectionBox);
    this.savedPointBudget = this.viewer.getPointBudget();
    this.viewer.setPointBudget(9_000_000);
  }

  protected onDisable(): void {
    this.viewer.renderArea.style.cursor = '';
    this.selectionBox.style.display = 'none';
    this.selectionBox.remove();
    this.viewer.setPointBudget(this.savedPointBudget);

    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.cancel();
    }

    if (this.isSelecting) {
      this.isSelecting = false;
      this.viewer.inputHandler.unblockOrbitControls();
    }
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.ORTHOGRAPHIC_2D_TOP],
      defaultViewId: 'orthographic_2d_top',
    };
  }

  // ─── Event handlers ────────────────────────────────────────────────

  public onClick(event: MouseEvent): void {
    const screenPos = this.getScreenPositionFromEvent(event);
    const worldPos = this.screenToWorld(screenPos);
    if (!worldPos) return;

    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.complete(worldPos, screenPos);
    } else {
      this.cameraAlignmentTool.start(worldPos, screenPos);
    }
  }

  public onMouseMove(event: MouseEvent): void {
    if (!this.cameraAlignmentTool.isAlignmentActive()) return;

    const screenPos = this.getScreenPositionFromEvent(event);
    const worldPos = this.screenToWorld(screenPos);
    if (worldPos) {
      this.cameraAlignmentTool.updateEndPoint(worldPos, screenPos);
    }
  }

  public onDragStart(data: DragEventData): void {
    if (data.button !== 0) return;
    if (this.cameraAlignmentTool.isAlignmentActive()) return;

    this.selectionStart = data.startScreen.clone();
    this.isSelecting = true;

    this.selectionBox.style.left = `${data.startScreen.x}px`;
    this.selectionBox.style.top = `${data.startScreen.y}px`;
    this.selectionBox.style.width = '0px';
    this.selectionBox.style.height = '0px';
    this.selectionBox.style.display = 'block';

    this.viewer.inputHandler.blockOrbitControls();
  }

  public onDrag(data: DragEventData): void {
    if (!this.isSelecting) return;

    const width = data.currentScreen.x - this.selectionStart.x;
    const height = data.currentScreen.y - this.selectionStart.y;

    this.selectionBox.style.width = `${Math.abs(width)}px`;
    this.selectionBox.style.height = `${Math.abs(height)}px`;
    this.selectionBox.style.left = `${width < 0 ? data.currentScreen.x : this.selectionStart.x}px`;
    this.selectionBox.style.top = `${height < 0 ? data.currentScreen.y : this.selectionStart.y}px`;
  }

  public onDragEnd(data: DragEventData): void {
    if (!this.isSelecting) return;

    this.isSelecting = false;
    this.viewer.inputHandler.unblockOrbitControls();

    this.flashEffect();

    const endScreen = data.currentScreen;

    const width = Math.abs(this.selectionStart.x - endScreen.x);
    const height = Math.abs(this.selectionStart.y - endScreen.y);

    const webglOrigin = new THREE.Vector2(Math.min(this.selectionStart.x, endScreen.x), Math.min(this.viewer.renderArea.clientHeight - this.selectionStart.y, this.viewer.renderArea.clientHeight - endScreen.y));

    this.viewer.scene.dispatchEvent({
      type: 'pointorama_ortho_screenshot',
      value: { origin: webglOrigin, width, height },
    });
  }

  public onRightClick(_event: MouseEvent): void {
    if (this.cameraAlignmentTool.isAlignmentActive()) {
      this.cameraAlignmentTool.cancel();
    } else if (this.isSelecting) {
      this.cancelSelection();
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.cameraAlignmentTool.isAlignmentActive()) {
        this.cameraAlignmentTool.cancel();
      } else if (this.isSelecting) {
        this.cancelSelection();
      }
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────

  private cancelSelection(): void {
    this.isSelecting = false;
    this.selectionBox.style.display = 'none';
    this.viewer.inputHandler.unblockOrbitControls();
  }

  private getScreenPositionFromEvent(event: MouseEvent): THREE.Vector2 {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  private screenToWorld(screenPos: THREE.Vector2): THREE.Vector3 | null {
    const camera = this.viewer.scene.getActiveCamera();
    if (!(camera instanceof THREE.OrthographicCamera)) {
      return null;
    }

    const rect = this.viewer.renderArea.getBoundingClientRect();
    const ndcX = (screenPos.x / rect.width) * 2 - 1;
    const ndcY = -(screenPos.y / rect.height) * 2 + 1;

    const ndc = new THREE.Vector3(ndcX, ndcY, 0);
    return ndc.unproject(camera);
  }

  private flashEffect(): void {
    this.selectionBox.style.transition = 'background 30ms ease-out, border 30ms ease-out';
    this.selectionBox.style.background = 'rgba(150, 150, 150, 0.5)';
    this.selectionBox.style.borderColor = 'rgba(255, 255, 255, 1)';

    setTimeout(() => {
      this.selectionBox.style.transition = 'background 300ms ease-out, border 300ms ease-out';
      this.selectionBox.style.background = 'rgba(100, 100, 100, 0)';
      this.selectionBox.style.borderColor = 'rgba(255, 255, 255, 0)';

      setTimeout(() => {
        this.selectionBox.style.display = 'none';

        this.selectionBox.style.borderColor = 'white';
        this.selectionBox.style.background = 'rgba(100, 100, 100, 0.2)';
      }, 300);
    }, 30);
  }
}
